import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import ViewListIcon from '@mui/icons-material/ViewList';
import IosShareIcon from '@mui/icons-material/IosShare';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { MaterialChunksDialog } from '../MaterialChunksDialog';
import { formatDate, KnowledgeDetails, KnowledgeSearchResult, TargetType } from '../../services/learningApi';
import { HelpTip, useApis } from './common';

interface Props {
  targetType: TargetType;
  targetId: string;
  /** Bump to reload (after entry changes). */
  refreshKey: number;
  onShowStatus: (status: string) => void;
  onApproveAllWaiting: () => void;
  /** Open the export dialog for every in-use entry. */
  onExportAll: (count: number) => void;
  /** Open the import dialog (copy entries from another function or chain). */
  onImport: () => void;
}

const copy = (text: string) => navigator.clipboard?.writeText(text).catch(() => undefined);

const STEP_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 90px', sm: 'minmax(180px, 1.2fr) 140px 2fr' },
  columnGap: 2,
  rowGap: 0.5,
  alignItems: 'center',
  px: 1.5,
  py: 0.75,
} as const;

export const KnowledgeBaseCard: React.FC<Props> = ({ targetType, targetId, refreshKey, onShowStatus, onApproveAllWaiting, onExportAll, onImport }) => {
  const apis = useApis();
  const [data, setData] = useState<KnowledgeDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [docOpen, setDocOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [snippetsOpen, setSnippetsOpen] = useState(false);

  const saveName = async () => {
    if (!nameDraft?.trim()) return;
    setRenaming(true);
    try {
      await apis.learning.renameCategory(targetType, targetId, nameDraft.trim());
      setNameDraft(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRenaming(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setData(await apis.learning.knowledge(targetType, targetId));
      setError(null);
    } catch (e: any) {
      setError(e.message);
    }
  }, [apis, targetType, targetId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Entry edits republish in the background; pick up the new snippet count shortly after.
  useEffect(() => {
    if (!refreshKey) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [refreshKey, load]);

  const republish = async () => {
    setPublishing(true);
    try {
      const res = await apis.learning.publish(targetType, targetId);
      setNotice(
        res.published_entries
          ? `Rebuilt ${res.published_entries} snippet${res.published_entries === 1 ? '' : 's'} from the in-use entries. No entries were changed.`
          : `Nothing to publish: no entries are in use yet${res.waiting_entries ? ` (${res.waiting_entries} waiting — approve them to use them now)` : ''}.`,
      );
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPublishing(false);
    }
  };

  if (error && !data) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!data) return <CircularProgress size={24} sx={{ mb: 2 }} />;

  const { usage, counts, thresholds } = data;
  const steps = usage.steps;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {usage.in_use ? <CheckCircleIcon color="success" sx={{ mt: 0.25 }} /> : <PauseCircleOutlineIcon color="warning" sx={{ mt: 0.25 }} />}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {usage.in_use ? 'Learned knowledge is in use' : 'Learned knowledge is not in use yet'}
          </Typography>
          {usage.in_use ? (
            <Typography variant="body2" color="text.secondary">
              Each {targetType === 'chain' ? 'step listed below' : 'call'} gets up to {usage.top_k} of the {data.live_chunks} published snippets:
              entries whose name appears in its input first, then ones at least {Math.round((thresholds.min_similarity ?? 0.35) * 100)}% similar.
              Unrelated inputs get nothing. Only entries marked “In use” are published.
            </Typography>
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {usage.blockers.map((b) => (
                <Typography key={b} component="li" variant="body2" color="text.secondary">
                  {b}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Tooltip title="Published and given to runs">
          <Chip color="success" label={`${counts.live} in use`} onClick={() => onShowStatus('active,pinned')} />
        </Tooltip>
        <Tooltip title="Learned, but not used until backed by more runs or approved by you">
          <Chip variant="outlined" label={`${counts.waiting} waiting`} onClick={() => onShowStatus('candidate')} />
        </Tooltip>
        {counts.rejected > 0 && <Chip variant="outlined" label={`${counts.rejected} rejected`} onClick={() => onShowStatus('rejected')} />}
        {counts.stale > 0 && <Chip variant="outlined" color="warning" label={`${counts.stale} stale`} onClick={() => onShowStatus('stale')} />}
      </Stack>

      {counts.waiting > 0 && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => onShowStatus('candidate')}>
                Review
              </Button>
              <Button size="small" onClick={onApproveAllWaiting}>
                Approve all
              </Button>
            </Stack>
          }
        >
          {counts.waiting} learning{counts.waiting === 1 ? ' is' : 's are'} waiting.{' '}
          {thresholds.auto_publish
            ? `Each goes live on its own once ${thresholds.min_evidence}+ runs back it up with ${Math.round(thresholds.min_confidence * 100)}%+ evidence strength. `
            : 'Auto-approve is off, so they only go live when you approve them. '}
          Read them below: approve the ones that are right, reject the ones that are wrong.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.25}>
        <Row label="Knowledge category" help="The Knowledge Base category holding these snippets. Use it to search or inspect them in Knowledge Base.">
          {data.category && nameDraft !== null ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setNameDraft(null);
                }}
                sx={{ minWidth: 320 }}
              />
              <Button size="small" variant="contained" onClick={saveName} disabled={!nameDraft.trim() || renaming}>
                Save
              </Button>
              <Button size="small" onClick={() => setNameDraft(null)} disabled={renaming}>
                Cancel
              </Button>
            </Stack>
          ) : data.category ? (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                {data.category.name}
              </Typography>
              <Tooltip title="Copy name">
                <IconButton size="small" onClick={() => copy(data.category!.name)}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rename. Safe at any time: runs and chain steps refer to the category by id, and publishing keeps your name.">
                <IconButton size="small" onClick={() => setNameDraft(data.category!.name)}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Link component={RouterLink} to={`/dashboard/materials?category=${data.category.id}`} underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}>
                Open in Knowledge Base <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Created after the first analysis.
            </Typography>
          )}
        </Row>
        {data.material && (
          <Row
            label="Document"
            help="Generated from the in-use entries by code, not written by AI, with one snippet per entry. Managed here, so it’s read-only in Knowledge Base."
          >
            <Typography variant="body2">
              {data.material.name} · {data.live_chunks} snippet{data.live_chunks === 1 ? '' : 's'} · last rebuilt {formatDate(data.published_at)}
            </Typography>
          </Row>
        )}
      </Stack>

      {steps && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            What each step receives
            <HelpTip
              title={
                usage.steps_source === 'settings'
                  ? 'Chosen in Settings → Step overrides.'
                  : usage.steps_source === 'profile'
                    ? 'Chosen by the learning profile (Profile → Steps that get knowledge). Override in Settings → Step overrides.'
                    : 'Every AI step (no step list set in the profile or Settings).'
              }
            />
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ ...STEP_GRID, bgcolor: 'action.selected' }}>
              <Typography variant="caption" fontWeight={600}>Step</Typography>
              <Typography variant="caption" fontWeight={600}>Learned knowledge</Typography>
              <Typography variant="caption" fontWeight={600}>Other knowledge categories (set in the chain editor)</Typography>
            </Box>
            {steps.map((s, i) => (
              <Box
                key={s.node_id}
                sx={{ ...STEP_GRID, borderTop: 1, borderColor: 'divider', bgcolor: i % 2 ? 'action.hover' : 'background.paper' }}
              >
                <Typography variant="body2" fontWeight={s.gets_learned ? 600 : 400} sx={{ wordBreak: 'break-word' }}>
                  {s.label}
                </Typography>
                {s.kind === 'plugin' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: 'span 2' }}>
                    Tool step: doesn’t use knowledge
                  </Typography>
                ) : (
                  <>
                    <Box>
                      {s.gets_learned ? (
                        <Chip size="small" color="primary" icon={<CheckCircleIcon />} label="Yes" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      {s.categories.length ? (
                        s.categories.map((c) => <Chip key={c.id} size="small" variant="outlined" label={c.name || 'Deleted category'} />)
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => setSearchOpen(!searchOpen)} disabled={!data.live_chunks}>
          Test search
        </Button>
        <Button size="small" variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => setDocOpen(true)} disabled={!data.document}>
          View document
        </Button>
        <Tooltip title="The exact snippets stored in the knowledge category, as runs receive them (same view as Knowledge Base → View contents)">
          <span>
            <Button size="small" variant="outlined" startIcon={<ViewListIcon />} onClick={() => setSnippetsOpen(true)} disabled={!data.material || !data.live_chunks}>
              View stored snippets
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Copy the in-use entries into a separate, frozen Knowledge Base file in a category you choose. To export only some, tick them in the table below.">
          <span>
            <Button size="small" variant="outlined" startIcon={<IosShareIcon />} onClick={() => onExportAll(counts.live)} disabled={!counts.live}>
              Export…
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Copy in-use entries from another function or chain in this account. They start without evidence here and never overwrite entries this one already has.">
          <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={onImport}>
            Import…
          </Button>
        </Tooltip>
        <Tooltip
          title={
            <>
              Rebuilds the knowledge category from the entries that are In use right now: regenerates the document and re-embeds every snippet.
              It doesn’t analyze runs or change any entry, and costs only embeddings.
              <br />
              <br />
              This already happens after every analysis and whenever you change an entry. Use it after a platform update, or if the snippet count
              looks wrong.
            </>
          }
        >
          <span>
            <Button size="small" startIcon={publishing ? <CircularProgress size={14} /> : <SyncIcon />} onClick={republish} disabled={publishing}>
              Rebuild snippets
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Collapse in={searchOpen} unmountOnExit>
        <SearchPanel targetType={targetType} targetId={targetId} defaultTopK={usage.top_k} minSimilarity={thresholds.min_similarity ?? 0.35} />
      </Collapse>

      {snippetsOpen && data.material && (
        <MaterialChunksDialog
          materialId={data.material.id}
          title={data.category?.name || data.material.name}
          intro="One snippet per in-use learning, with how many runs back it up. This is exactly what a run can receive."
          onClose={() => setSnippetsOpen(false)}
        />
      )}
      {docOpen && data.document && <DocumentDialog title={data.material?.name || 'Knowledge document'} fileName={data.material?.file_name} doc={data.document} onClose={() => setDocOpen(false)} />}
    </Paper>
  );
};

const Row: React.FC<{ label: string; help: string; children: React.ReactNode }> = ({ label, help, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} alignItems={{ sm: 'center' }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 170 }}>
      {label}
      <HelpTip title={help} />
    </Typography>
    <Box>{children}</Box>
  </Stack>
);

const SearchPanel: React.FC<{ targetType: TargetType; targetId: string; defaultTopK: number; minSimilarity: number }> = ({
  targetType,
  targetId,
  defaultTopK,
  minSimilarity,
}) => {
  const apis = useApis();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KnowledgeSearchResult[] | null>(null);
  const usedCount = results ? results.filter((r) => r.used).length : 0;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apis.learning.searchKnowledge(targetType, targetId, query.trim());
      setResults(res.results);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Type or paste an input the way a run would see it (a product name, or the whole input). This runs the same lookup as a real run: entries
        whose name appears in the input come first, then meaning-based matches above the cutoff, up to {defaultTopK}.
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="e.g. barilla pasta"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && run()}
        InputProps={{
          sx: { bgcolor: 'background.paper' },
          endAdornment: (
            <InputAdornment position="end">
              <Button size="small" onClick={run} disabled={busy || !query.trim()}>
                {busy ? <CircularProgress size={16} /> : 'Search'}
              </Button>
            </InputAdornment>
          ),
        }}
      />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {results && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {usedCount === 0
            ? 'A run with this input would get no learned knowledge.'
            : `A run with this input would get ${usedCount} snippet${usedCount === 1 ? '' : 's'}.`}
          {results.some((r) => !r.used) && ' Greyed results are shown for comparison and would not be used.'}
        </Typography>
      )}
      {results && results.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {results.map((r, i) => (
            <Paper key={`${r.entry_id}-${i}`} variant="outlined" sx={{ p: 1.25, opacity: r.used ? 1 : 0.55 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                {r.match === 'exact' ? (
                  <Tooltip title={`“${r.matched}” appears in your input, so this entry is always included (ahead of similarity matches).`}>
                    <Chip size="small" color="success" label="Exact name match" />
                  </Tooltip>
                ) : (
                  <Tooltip title={`Meaning-based match. Used when at least ${Math.round(minSimilarity * 100)}% similar (Settings → relevance cutoff).`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={r.used ? 'primary' : 'default'}
                      label={r.similarity !== null ? `${Math.round(r.similarity * 100)}% similar` : 'Similar'}
                    />
                  </Tooltip>
                )}
                {!r.used && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={r.reason === 'below_cutoff' ? 'Below relevance cutoff' : `Beyond the ${defaultTopK} per run`}
                  />
                )}
                {r.key && <Typography variant="caption" color="text.secondary">{r.kind === 'fact' ? 'Fact' : r.key}</Typography>}
              </Stack>
              <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {r.text}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const DocumentDialog: React.FC<{ title: string; fileName?: string; doc: string; onClose: () => void }> = ({ title, fileName, doc, onClose }) => {
  const download = () => {
    const url = URL.createObjectURL(new Blob([doc], { type: 'text/markdown' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'auto_learned_knowledge.md';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This is the full document for the in-use entries. Each entry below is stored as one snippet in the knowledge category, so
          it is never split in half.
        </Typography>
        <Box component="pre" sx={{ m: 0, p: 2, bgcolor: 'action.hover', borderRadius: 1, fontSize: 13, whiteSpace: 'pre-wrap', maxHeight: '60vh', overflow: 'auto' }}>
          {doc}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={() => copy(doc)}>
          Copy
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={download}>
          Download .md
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
